// SPDX-License-Identifier: Elastic-2.0
/**
 * LanguageSection — Settings section for the active UI locale (T-028).
 *
 * Renders a styled native `<select>` with the 6 supported locales.
 * Switching the value immediately calls `useLocale.setLocale()`, which
 * lazy-loads the locale chunk, flips i18next's active language, and
 * mirrors `<html lang>`. The change is fire-and-forget — the promise is
 * intentionally not awaited because i18next falls back to EN until the
 * chunk lands, which matches the design (no flash of missing text).
 */
import { useTranslation } from "react-i18next";

import { Card } from "@/components/Card/Card";
import { useLocale } from "@/i18n/useLocale";
import type { SupportedLocale } from "@/i18n";

import styles from "./LanguageSection.module.css";

/**
 * Display names for each supported locale. Each label is the
 * **endonym** (the language's name in its own language) so users
 * recognise their preferred entry regardless of what UI locale is
 * active. Matches the convention used by major OS settings panels.
 */
const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: "English",
  es: "Español",
  pt: "Português",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
};

export function LanguageSection(): JSX.Element {
  const { t } = useTranslation();
  const locale = useLocale((s) => s.locale);
  const supported = useLocale((s) => s.supported);
  const setLocale = useLocale((s) => s.setLocale);

  function handleChange(event: React.ChangeEvent<HTMLSelectElement>): void {
    const value = event.target.value as SupportedLocale;
    // The store ignores unsupported values; the runtime cast above just
    // narrows the type. Fire-and-forget — see file-level comment.
    void setLocale(value);
  }

  return (
    <Card as="section" variant="elevated" padding="md" data-testid="settings-language">
      <h2 className={styles.title}>{t("settings.language.section_title")}</h2>
      <div className={styles.row}>
        <select
          className={styles.select}
          value={locale}
          onChange={handleChange}
          aria-label={t("settings.language.section_title")}
          data-testid="settings-language-select"
        >
          {supported.map((code) => (
            <option key={code} value={code}>
              {LOCALE_LABELS[code]}
            </option>
          ))}
        </select>
      </div>
    </Card>
  );
}

export default LanguageSection;
